/**
 * The media library as rows.
 *
 * Rows are pills, not table cells. This was one of the last screens still
 * rendering the pre-redesign `<table>`, and it had that table's two habits: a
 * 500-weight column head where the design asks for 800, and a row whose only
 * action was a bin that faded in on hover - so with a keyboard the row was
 * readable and nothing more.
 *
 * Six columns became four tracks. Kind and size were separate cells saying
 * "image/png" and "312 KB" about a file whose name already ended in `.png`; the
 * kind is the row's subtitle now, which is where every other CRM list puts the
 * thing that identifies a row after its name.
 */
import { Folder } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { MediaAsset } from "../../../lib/media";
import { listCard } from "../../ui";
import { MediaRowMenu } from "./MediaRowMenu";
import { MediaThumb } from "./MediaThumb";
import { extLabel, formatBytes } from "./lib";

/** Name, size, folder, actions - one list for both the head and the rows. */
const COLS = "minmax(0,2.8fr) .9fr 1.2fr 44px";

export function MediaList({
  items,
  folderLabel,
  onDelete,
}: {
  items: MediaAsset[];
  folderLabel: (f: string) => string;
  onDelete: (m: MediaAsset) => void;
}) {
  const t = useT();

  return (
    <>
      <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS, paddingTop: 22 }}>
        <span>{t("media.colName")}</span>
        <span>{t("media.colSize")}</span>
        <span>{t("media.colFolder")}</span>
        <span />
      </div>

      <div className="crm-rows pt-0" style={listCard(COLS)}>
        {items.map((m) => (
          <div key={m.id} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
            <span className="flex min-w-0 items-center gap-3">
              <span className="relative size-10 shrink-0 overflow-hidden rounded-xl bg-muted">
                <MediaThumb asset={m} playMark="bare" />
              </span>
              <span className="min-w-0">
                {/* The file itself, in a new tab. A row in a media library has
                    nowhere else to go, and the name is the obvious handle for
                    it - which also puts the row in the tab order, where the
                    `<tr>` version of this list never was. */}
                <a
                  href={m.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={m.originalFilename}
                  className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full"
                >
                  {m.originalFilename}
                </a>
                <span className="crm-prow-s crm-trunc block">{extLabel(m)}</span>
              </span>
            </span>

            <span className="crm-mut crm-tnum text-[12.5px]">{formatBytes(m.sizeBytes)}</span>

            <span className="crm-mut crm-trunc flex items-center gap-1.5 text-[12.5px]">
              <Folder className="size-3.5 shrink-0" />
              <span className="crm-trunc">{folderLabel(m.folder)}</span>
            </span>

            <MediaRowMenu asset={m} onDelete={() => onDelete(m)} />
          </div>
        ))}
      </div>
    </>
  );
}
